import { isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { type ReactElement, useCallback } from 'react';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useCheckFlexiForwardByTradeCaptureMutation } from '@/store/flexi-forward/api';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import { notionalCurrencyChanged } from '@/store/flexi-forward/slice.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { TcValidation } from '@/store/types.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export function NotionalField({ tileId, validation }: { tileId: string; validation?: TcValidation[] }): ReactElement {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const [checkFlexiForward] = useCheckFlexiForwardByTradeCaptureMutation();

  const { currency1, currency2, notional, notionalCurrencyIndex, isNotionalCurrency2, tradeCapture } =
    useFlexiForwardSelector((flexiForward) => {
      const notionalCurrencyIndex = flexiForward.properties.notionalCurrencyIndex ?? 1;

      return {
        currency1: flexiForward.properties.currency1,
        currency2: flexiForward.properties.currency2,
        notional: flexiForward.properties.notional,
        notionalCurrencyIndex,
        isNotionalCurrency2: notionalCurrencyIndex === 2,
        tradeCapture: flexiForward.tradeCapture,
      };
    });

  const handleNotionalChanged = useFlexiForwardPropertyChangedHandler<number | undefined>(
    tileId,
    'Notional amount change',
    (value) => ({
      notional: isDefined(value) ? value : null,
      notionalCurrencyIndex,
    }),
  );

  const handleNotionalCurrencyClick = useCallback(() => {
    if (isNotDefined(tradeCapture)) {
      return;
    }

    const currencyIndex: CurrencyIndex = notionalCurrencyIndex === 1 ? 2 : 1;
    const currency = currencyIndex === 1 ? currency1 : currency2;
    sgwtWebAnalytics?.trackEvent('Flexi Forward', 'Notional currency change', currency);

    dispatch(notionalCurrencyChanged({ tileId, notionalCurrencyIndex: currencyIndex }));

    checkFlexiForward({
      tileId,
      tradeCapture,
      properties: {
        notional,
        notionalCurrencyIndex: currencyIndex,
        marginCurrency: currency,
      },
    });
  }, [
    notionalCurrencyIndex,
    tileId,
    tradeCapture,
    checkFlexiForward,
    notional,
    currency1,
    currency2,
    sgwtWebAnalytics,
  ]);

  return (
    <FlexiForwardFormField formattedMessageId="flexiForwardTile.mainFields.notional.label">
      <TradeCaptureValidationWrapper validation={validation}>
        <SgAmountInput
          value={notional !== null ? notional : undefined}
          onChange={handleNotionalChanged}
          unit={!isNotionalCurrency2 ? currency1 : currency2}
          unitBackgroundColor={isNotionalCurrency2 ? 'warning' : undefined}
          onUnitClick={handleNotionalCurrencyClick}
          size="sm"
          selectAllOnFocus
          data-e2e="notional"
        />
      </TradeCaptureValidationWrapper>
    </FlexiForwardFormField>
  );
}
